import React, { useState, useEffect, useRef } from "react";
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Linkedin,
  Globe,
  Edit3,
  Trash2,
  FileText,
  Plus,
  Eye,
  Download,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useProfile } from "../hooks/useProfile";
import { useCV, StoredCV } from "../hooks/useCV";
import { useFiles } from "../hooks/useFiles";
import Loading from "./sub-components/loading";
import { useSubscriptionStore } from "../stores/subscription.store";
import DataDownloadModal from "./DataDownloadModal";
import { supabase } from "../lib/supabase";

interface ProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  handlePackagesModal: () => void;
}

const ProfileModal: React.FC<ProfileModalProps> = ({
  isOpen,
  onClose,
  handlePackagesModal,
}) => {
  const {
    profile,
    updateProfile,
    updateCurrentCv,
    loading: profileLoading,
  } = useProfile();
  const { cvs, refetch: refetchCVs, loading: cvsLoading } = useCV();
  const { subscription, loading: subscriptionLoading } = useSubscriptionStore();
  const { t } = useTranslation();
  const { uploadFile, deleteCV, downloadCV, loading: fileActionLoading } = useFiles();

  const [profileData, setProfileData] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    linkedin: "",
    website: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // CV Management State
  const [tempSelectedCvId, setTempSelectedCvId] = useState<string | null>(null);
  const [isSavingSelection, setIsSavingSelection] = useState(false);
  const [isDeletingCv, setIsDeletingCv] = useState(false);
  const [cvToBeDeleted, setCvToBeDeleted] = useState<String | null>(null);
  const [isViewingCv, setIsViewingCv] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Data Download Modal State
  const [isDataDownloadModalOpen, setIsDataDownloadModalOpen] = useState(false);

  // Determine CV limit based on subscription
  // console.log("subscription", subscription);

  const isFreeTier = !subscription || subscription.plan === "free";

  useEffect(() => {
    if (error !== null) {
      const timer = setTimeout(() => setError(null), 1000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  useEffect(() => {
    if (success !== null) {
      const timer = setTimeout(() => setSuccess(null), 1000);
      return () => clearTimeout(timer);
    }
  }, [success]);


  const effectiveSubscription = subscription;

  // useEffect(() => {
  // 	if (isOpen && user?.id) {
  // 		// refetchCVs();
  // 		// refetchSubscription();
  // 		// fetchProfile();
  // 	}
  // }, [isOpen]);

  useEffect(() => {
    if (profile && isOpen) {
      setProfileData({
        name: profile.name || "",
        email: profile.email || "",
        phone: profile.phone || "",
        address: profile.address || "",
        linkedin: profile.linkedin || "",
        website: profile.website || "",
      });
      setIsEditing(!profile.name && !profile.email);
      setTempSelectedCvId(profile.current_cv || null);
    }
  }, [isOpen, profile]);



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);

    try {
      await updateProfile(profileData);
      setIsEditing(false);
      setHasChanges(false);
    } catch (error) {
      console.error(t("profile.save_error"), error);
    } finally {
      setIsSaving(false);
    }
  };
  const handleClearProfile = async () => {
    if (confirm(t("profile.clear_confirm"))) {
      try {
        await updateProfile({
          name: "",
          phone: "",
          address: "",
          linkedin: "",
          website: "",
        });
        setProfileData({
          name: "",
          email: profileData.email, // Keep email
          phone: "",
          address: "",
          linkedin: "",
          website: "",
        });
        setHasChanges(false);
      } catch (error) {
        console.error(t("profile.clear_error"), error);
      }
    }
  };

  // --- New CV Handlers ---
  const handleCvClick = (cvId: string) => setTempSelectedCvId(cvId);

  const handleViewCv = async (cv: StoredCV) => {
    setIsViewingCv(cv.id);
    try {
      // Check if content is an S3 key or old Supabase URL
      const { s3Service } = await import('../services/s3Service');
      
      if (s3Service.isS3Key(cv.content)) {
        // Handle S3 files - get presigned URL
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.access_token) {
          throw new Error('No valid session found');
        }
        
        const presignedUrl = await s3Service.getPresignedUrl(cv.content, session.access_token, 3600);
        window.open(presignedUrl, '_blank');
      } else {
        // Handle old Supabase URLs (for migration compatibility)
        const urlParts = cv.content.split('/cv-match/');
        if (urlParts.length < 2) {
          throw new Error('Invalid CV URL format');
        }
        
        const filePath = decodeURIComponent(urlParts[1]);
        
        // Generate signed URL for secure access
        const { data: signedUrlData, error } = await supabase.storage
          .from('cv-match')
          .createSignedUrl(filePath, 3600); // 1 hour expiry
        
        if (error) {
          // Handle specific storage errors
          if (error.message?.includes('not found') || error.message?.includes('404')) {
            throw new Error('CV file not found. It may have been moved or deleted.');
          } else if (error.message?.includes('Bucket not found')) {
            throw new Error('Storage configuration error. Please contact support.');
          }
          throw error;
        }
        
        if (!signedUrlData?.signedUrl) {
          throw new Error('Failed to generate signed URL');
        }
        
        // Open CV in new tab using the signed URL
        window.open(signedUrlData.signedUrl, '_blank');
      }
      
    } catch (error) {
      console.error('Error viewing CV:', error);
      alert('Unable to view CV: ' + (error instanceof Error ? error.message : 'Unknown error'));
    } finally {
      setIsViewingCv(null);
    }
  };

  const handleDownloadCv = async (cv: StoredCV) => {
    try {
      const result = await downloadCV(cv);
      if (!result.success) {
        alert('Download failed: ' + (result.error?.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error downloading CV:', error);
      alert('Download failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
    }
  };

  const handleAddCvClick = () => {
    if (isFreeTier) {
      if (cvs?.length >= 1) {
        if (confirm(t("profile.wantToAddMultipleCVs"))) {
          onClose();
          handlePackagesModal();
        }
      } else {
        fileInputRef.current?.click();
      }
    } else {
      fileInputRef.current?.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadFile(file, refetchCVs);
    if (e.target) e.target.value = "";
  };

  const handleDeleteCv = async (e: React.MouseEvent, cv: StoredCV) => {
    e.stopPropagation();
    if (
      window.confirm(t("profile.cv_delete_confirm", { filename: cv.filename }))
    ) {
      setIsDeletingCv(true);
      setCvToBeDeleted(cv?.id);
      await deleteCV(cv, refetchCVs);
      setIsDeletingCv(false);
      setCvToBeDeleted(null);
    }
  };

  const handleSelectCv = async () => {
    if (!tempSelectedCvId || tempSelectedCvId === profile?.current_cv) return;
    setIsSavingSelection(true);
    await updateCurrentCv(tempSelectedCvId);
    setIsSavingSelection(false);
    // setSuccess('CV selection saved!');
    // onClose();
  };

  // --- Other Handlers ---
  // const handleCancelSubscription = async () => {
  //   try {
  //     setCancelLoading(true);
  //     // console.log("sess token : ",session?.access_token)
  //     const res = await axios.post(
  //       "https://jlyprvwmitisfutelyrj.supabase.co/functions/v1/cancel-subscription",
  //       {},
  //       {
  //         headers: {
  //           Authorization: `Bearer ${session?.access_token}`,
  //           "Content-Type": "application/json",
  //         },
  //       }
  //     );
  //     if (res?.data?.code === 200 && res?.data?.isSuccess === true) {
  //       setSuccess(res?.data?.message);
  //     }
  //   } catch (error) {
  //     if (axios.isAxiosError(error)) {
  //       setError(error?.message);
  //     } else {
  //       setError("An unknown error occurred");
  //     }
  //   } finally {
  //     setCancelLoading(false);
  //   }
  // };
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto mx-2 sm:mx-4">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center min-w-0 flex-1">
            <User className="w-5 h-5 sm:w-6 sm:h-6 text-indigo-600 mr-2 sm:mr-3 flex-shrink-0" />
            <div className="min-w-0 flex-1">
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900 truncate">
                {t("profile.title")}
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1 hidden sm:block">
                {t("profile.subtitle")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0 ml-2"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        <div className="p-4 sm:p-6 space-y-6 sm:space-y-8">
          {/* Subscription information */}
          {!effectiveSubscription && subscriptionLoading ? (
            <div className="bg-gray-50 rounded-md w-full py-5 flex justify-center items-center flex-col">
              <Loading color="gray" />
              <p className="text-gray-600">
                {t("profile.subscription.loading_subscription")}
              </p>
            </div>
          ) : (
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 sm:p-6 border border-indigo-200">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <div
                  className={`w-3 h-3 rounded-full mr-2 flex-shrink-0 ${
                    effectiveSubscription?.is_active
                      ? "bg-green-500"
                      : "bg-red-500"
                  }`}
                ></div>
                <span className="truncate">
                  {t("profile.subscription.title")}
                </span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                {/* <div>
                  <div className="text-xs sm:text-sm text-gray-600">
                    {t("profile.subscription.current_plan")}
                  </div>
                  <div className="font-semibold text-gray-900 text-sm sm:text-base truncate">
                    {currentPlan?.name}{" "}
                    {currentPlan?.price
                      ? `- ${formatPrice(currentPlan.price)}`
                      : `(${t("profile.subscription.free")})`}
                  </div>
                </div> */}

                <div>
                  <div className="text-xs sm:text-sm text-gray-600">
                    {t("profile.subscription.analyses_used")}
                  </div>
                  <div className="font-semibold text-gray-900 text-sm sm:text-base">
                    {effectiveSubscription?.analyses_used} /{" "}
                    {(effectiveSubscription?.analyses_remaining ?? 0) + (effectiveSubscription?.analyses_used ?? 0)}
                  </div>
                </div>

                <div>
                  <div className="text-xs sm:text-sm text-gray-600">
                    {t("profile.subscription.analyses_remaining")}
                  </div>
                  <div className="font-semibold text-gray-900 text-sm sm:text-base">
                    {(effectiveSubscription?.analyses_remaining ?? 0)}
                  </div>
                </div>

                <div>
                  {/* <div className="text-xs sm:text-sm text-gray-600">
                    {t("profile.subscription.status")}
                  </div>
                  <div
                    className={`font-semibold text-sm sm:text-base ${
                      effectiveSubscription?.is_active
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {effectiveSubscription?.is_active
                      ? t("profile.subscription.active")
                      : t("profile.subscription.inactive")}
                  </div> */}
                </div>
              </div>
              {error && <p className="text-red-500">{error}</p>}
              {success && <p className="text-green-500">{success}</p>}
            </div>
          )}
          {/* New My Saved CV section */}
          <div className="bg-gradient-to-br from-gray-50 to-indigo-50 rounded-xl p-6 border border-gray-200">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-indigo-600" />
              {cvs?.length > 0 ? t("profile.cv.saved_cv") : t("profile.cv.no_cv")}
            </h3>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileChange}
              className="hidden"
              accept=".pdf,.doc,.docx,.txt"
            />

            {cvsLoading ? (
              <div className="text-center p-12 bg-white rounded-lg border border-gray-200">
                <Loading color="gray" />
                <p className="mt-4 text-gray-600">{t("profile.cv.loading_cv")}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {cvs?.length === 0 ? (
                  <div className="text-center p-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                    <FileText className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                    <h4 className="text-lg font-medium text-gray-900 mb-2">
                      {t("profile.cv.no_cv_title")}
                    </h4>
                    <p className="text-gray-600 mb-6">
                      {t("profile.cv.no_cv_description")}
                    </p>
                    <button
                      onClick={handleAddCvClick}
                      disabled={fileActionLoading}
                      className="inline-flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {!fileActionLoading && (
                        <Plus className="w-5 h-5 mr-2" />
                      )}
                      {fileActionLoading
                        ? t("profile.cv.uploading")
                        : t("profile.cv.add_first_cv")}
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 min-h-[120px]">
                      {cvs.map(cv => {
                        const isSelected = cv.id === tempSelectedCvId;
                        const cardStyle = {
                          backgroundColor: isSelected ? "#F3EEFF" : "#FFFFFF",
                          borderColor: isSelected ? "#763BDE" : "#E5E7EB",
                        };

                        return (
                          <div
                            key={cv.id}
                            onClick={() => handleCvClick(cv.id)}
                            className="flex justify-between items-center p-6 rounded-xl border-2 cursor-pointer transition-all hover:shadow-md"
                            style={cardStyle}
                          >
                            <div className="flex items-center min-w-0">
                              <FileText className="w-8 h-8 mr-4 text-gray-600 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="font-semibold text-gray-800 truncate text-base">
                                  {cv.filename}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                  {t("profile.cv.last_updated")}:{" "}
                                  {new Date(cv.updated_at).toLocaleDateString()}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 ml-3">
                              <button
                                onClick={() => handleViewCv(cv)}
                                disabled={isViewingCv === cv.id}
                                className="p-2 text-gray-500 hover:text-blue-500 hover:bg-blue-50 rounded-lg flex-shrink-0 disabled:opacity-50 transition-colors"
                                title={t("profile.cv.view")}
                              >
                                {isViewingCv === cv.id ? (
                                  <Loading color="gray" />
                                ) : (
                                  <Eye className="w-5 h-5" />
                                )}
                              </button>
                              <button
                                onClick={() => handleDownloadCv(cv)}
                                disabled={fileActionLoading}
                                className="p-2 text-gray-500 hover:text-green-500 hover:bg-green-50 rounded-lg flex-shrink-0 disabled:opacity-50 transition-colors"
                                title="Download CV"
                              >
                                <Download className="w-5 h-5" />
                              </button>
                              <button
                                disabled={isDeletingCv}
                                onClick={e => handleDeleteCv(e, cv)}
                                className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0 transition-colors"
                                title={t("profile.cv.delete")}
                              >
                                {isDeletingCv && cvToBeDeleted === cv.id ? (
                                  <Loading color="green" />
                                ) : (
                                  <Trash2 className="w-5 h-5" />
                                )}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {cvs?.length < 3 && (
                        <button
                          onClick={handleAddCvClick}
                          disabled={fileActionLoading}
                          className="flex flex-col justify-center items-center p-8 rounded-xl border-2 border-dashed border-gray-400 text-gray-600 hover:bg-gray-50 hover:border-indigo-400 transition-all min-h-[120px]"
                        >
                          <Plus className="w-8 h-8 mb-3" />
                          <span className="font-medium">
                            {fileActionLoading
                              ? t("profile.cv.uploading")
                              : t("profile.cv.add")}
                          </span>
                        </button>
                      )}
                    </div>

                    {!isFreeTier && cvs?.length > 0 && (
                      <div className="flex flex-col md:flex-row gap-4 mt-8">
                        <button
                          onClick={handleSelectCv}
                          disabled={
                            !tempSelectedCvId ||
                            isSavingSelection ||
                            tempSelectedCvId === profile?.current_cv
                          }
                          className="w-full text-white font-semibold py-4 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                          style={{
                            background:
                              "linear-gradient(to right, #5042DA, #8039DF)",
                          }}
                        >
                          {isSavingSelection
                            ? t("profile.cv.saving_selection") 
                            : tempSelectedCvId === profile?.current_cv ? t("profile.cv.selected_cv")
                            : t("profile.cv.select")}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
          {profileLoading ? (
            <div className="bg-gray-50 rounded-md w-full py-5 flex justify-center items-center flex-col">
              <Loading color="gray" />
              <p className="text-gray-600">{t("profile.loading_profile")}</p>
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t("profile.personal_info.title")}
                </h3>
                <div className="flex space-x-2">
                  {!isEditing ? (
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setIsEditing(true)}
                        className="flex items-center justify-center px-3 py-2 text-xs sm:text-sm bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg transition-colors w-full sm:w-auto"
                      >
                        <Edit3 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                        {t("profile.personal_info.edit")}
                      </button>
                      {(profileData.name || profileData.phone) && (
                        <button
                          onClick={handleClearProfile}
                          className="flex items-center justify-center px-3 py-2 text-xs sm:text-sm bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors w-full sm:w-auto"
                        >
                          <Trash2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                          {t("profile.personal_info.clear")}
                        </button>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={handleSave}
                      disabled={isSaving || !hasChanges}
                      className="flex items-center justify-center px-3 py-2 text-xs sm:text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                    >
                      {isSaving ? (
                        <div className="flex items-center space-x-1 text-xs sm:text-sm">
                          <div className="w-3 h-3 sm:w-4 sm:h-4 border-2 border-green-600 border-t-transparent rounded-full animate-spin mr-1"></div>
                          {t("profile.personal_info.saving")}...
                        </div>
                      ) : (
                        <div>{t("profile.personal_info.save")}</div>
                      )}
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      <User className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                      {t("profile.personal_info.full_name")}
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="name"
                        value={profileData.name}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder={t(
                          "profile.personal_info.full_name_placeholder"
                        )}
                      />
                    ) : (
                      <div className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900 text-sm sm:text-base">
                        {profileData.name ||
                          t("profile.personal_info.not_provided")}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      <Mail className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                      {t("profile.personal_info.email")}
                    </label>
                    <div className="px-3 py-2 bg-gray-100 rounded-lg text-gray-600 text-sm sm:text-base">
                      <span className="truncate block">
                        {profileData.email ||
                          t("profile.personal_info.not_provided")}
                      </span>
                      <span className="text-xs ml-2">
                        ({t("profile.personal_info.not_editable")})
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      <Phone className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                      {t("profile.personal_info.phone")}
                    </label>
                    {isEditing ? (
                      <input
                        type="tel"
                        name="phone"
                        value={profileData.phone}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder={t(
                          "profile.personal_info.phone_placeholder"
                        )}
                      />
                    ) : (
                      <div className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900 text-sm sm:text-base">
                        {profileData.phone ||
                          t("profile.personal_info.not_provided")}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      <MapPin className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                      {t("profile.personal_info.address")}
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        name="address"
                        value={profileData.address}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder={t(
                          "profile.personal_info.address_placeholder"
                        )}
                      />
                    ) : (
                      <div className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900 text-sm sm:text-base">
                        {profileData.address ||
                          t("profile.personal_info.not_provided")}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      <Linkedin className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                      Linkedin
                    </label>
                    {isEditing ? (
                      <input
                        type="url"
                        name="linkedin"
                        value={profileData.linkedin}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder={t(
                          "profile.personal_info.linkedin_placeholder"
                        )}
                      />
                    ) : (
                      <div className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900 text-sm sm:text-base">
                        {profileData.linkedin ? (
                          <a
                            href={profileData.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-800 underline truncate block"
                          >
                            {profileData.linkedin}
                          </a>
                        ) : (
                          t("profile.personal_info.not_provided")
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                      <Globe className="w-3 h-3 sm:w-4 sm:h-4 inline mr-1" />
                      {t("profile.personal_info.website")}
                    </label>
                    {isEditing ? (
                      <input
                        type="url"
                        name="website"
                        value={profileData.website}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        placeholder={t(
                          "profile.personal_info.website_placeholder"
                        )}
                      />
                    ) : (
                      <div className="px-3 py-2 bg-gray-50 rounded-lg text-gray-900 text-sm sm:text-base">
                        {profileData.website ? (
                          <a
                            href={profileData.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-indigo-600 hover:text-indigo-800 underline truncate block"
                          >
                            {profileData.website}
                          </a>
                        ) : (
                          t("profile.personal_info.not_provided")
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {isEditing && (
                  <div className="bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-200">
                    <div className="flex items-start">
                      <div className="text-blue-500 mr-3 mt-1 flex-shrink-0">
                        <svg
                          className="w-4 h-4 sm:w-5 sm:h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="font-semibold text-blue-900 mb-1 text-sm sm:text-base">
                          {t("profile.personal_info.auto_save_title")}
                        </h4>
                        <p className="text-xs sm:text-sm text-blue-800">
                          {t("profile.personal_info.auto_save_description")}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 sm:px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors text-sm sm:text-base w-full lg:w-auto"
          >
            {t("profile.close")}
          </button>
        </div>
      </div>

      {/* Data Download Modal */}
      <DataDownloadModal
        isOpen={isDataDownloadModalOpen}
        onClose={() => setIsDataDownloadModalOpen(false)}
      />

    </div>
  );
};

export default ProfileModal;
